import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { Container, Row, Col, Button, Input, Label, Card, CardBody, CardHeader, Table, Collapse, Alert } from "reactstrap";
import DataTable from "react-data-table-component";
import { toast, ToastContainer } from "react-toastify";
import { FadeLoader } from "react-spinners";

// Components
import GeneralSettings from "../../Components/Common/GeneralSettings";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import ActionButton from "../../Components/Common/ActionButton";
import BatchConditionSelector from "../../Components/Common/BatchConditionSelector";
import BatchJobPairsSummary from "../../Components/Common/BatchJobPairsSummary";
import BatchProgressCard from "../../Components/Common/BatchProgressCard";

// Config
import {
  codonOccupancyConfig,
  codonOccupancyDefaults,
  codonOccupancyValidation,
} from "../../config/plotConfigs/codonOccupancyConfig";

// API 
import {
  submitCodonOccupancyJob,
  fetchJobStatus,
  getSampleGroupingsData,
  postCreateBatchCode,
  postCreateBatchDownload,
  getPlusStatus,
} from "../../helpers/eirna_backend_helper";


// Short code handling
import {
  useCodonOccupancyShortCode,
  createPlotSpecificPayload,
} from "../../hooks/useShortCodeHandlers";


// Hooks
import { useGroupsManager } from "../../hooks/useGroupsManager";
import { useSettings } from "../../hooks/useSettings";
import { useScrollToRef } from "../../hooks/useScrollToRef";
import { EnterButtonListener } from "../../hooks/EnterButtonListener";
import { useBatchConditions } from "../../hooks/useBatchConditions";
import { useBatchPolling } from "../../hooks/useBatchPolling";


// Utils
import { filterFilesByType,
         dataTableStyles,
         testNumericVal,
         testNumericCol,
         customColumnSort 
} from "../../utils/batchUtils"


const COLUMNS = [
  { key: "cond1",          label: "Cond1 name"},
  { key: "cond2",          label: "Cond2 name"},
  { key: "top_codon_1",    label: "Top codon 1"},
  { key: "top_codon_1_count",    label: "Top codon 1 count"},
  { key: "top_codon_2",    label: "Top codon 2"},
  { key: "top_codon_2_count",    label: "Top codon 2 count"},
  { key: "top_codon_3",    label: "Top codon 3"},
  { key: "top_codon_3_count",    label: "Top codon 3 count"},
];

const formatResultRow = (rawRow) => {
    const formattedRow = { ...rawRow };
    
    // Flatten the top_codons object into the expected column keys
    if (rawRow.top_codons) {
      formattedRow.top_codon_1 = rawRow.top_codons[0]?.codon || "";
      formattedRow.top_codon_1_count = rawRow.top_codons[0]?.count ?? "";
      
      formattedRow.top_codon_2 = rawRow.top_codons[1]?.codon || "";
      formattedRow.top_codon_2_count = rawRow.top_codons[1]?.count ?? "";
      
      formattedRow.top_codon_3 = rawRow.top_codons[2]?.codon || "";
      formattedRow.top_codon_3_count = rawRow.top_codons[2]?.count ?? "";
    }
    
    return formattedRow;
  };


const BatchCodonOccupancy = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    loading,
    apiData,
    availableConditions,
    existingGroups,
    selectedCond1,
    selectedCond2,
    jobPairs,
    handleToggle,
    handleSelectAll,
  } = useBatchConditions(projectId);

  const {
    groups,
    setGroups,
    addFilesToGroup,
    removeFilesFromGroup,
    updateGroupName,
    clearGroup,
    autoLabelGroups,
    clearAllGroups,
    validateGroupsForDE,
    getMasterFileDict,
  } = useGroupsManager();
  
  // Settings
  const [emailNotification, setEmailNotification] = useState(false);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [showPairsDropdown, setShowPairsDropdown] = useState(false);
  const [plusStatus, setPlusStatus] = useState(null); 

  const analysisMode = 'comparison';


  // Results & Tracking
  const [filterText, setFilterText] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);





  // Refs for scrolling
  const resultsRef = useRef(null);

  // Custom hooks
  const {
    settings,
    updateSetting,
    resetSettings,
    validateSettings,
    updateMultipleSettings,
  } = useSettings(codonOccupancyDefaults, codonOccupancyValidation);



  const visibleColumns = COLUMNS;

  const {
    batchCode,
    setBatchCode,
    results,
    setResults,
    activeTasks,
    setActiveTasks,
    batchLoading,
    setBatchLoading,
    pollTaskStatus,
    initNewBatch,
    copyBatchURLToClipboard
  } = useBatchPolling(formatResultRow);

  const interactiveRows = useMemo(
    () => results.map((row, index) => ({
      id: `${row.short_code || "result"}-${index}`,
      ...row,
    })),
    [results]
  );

  // A callback used by the EnterButtonListener to generate the plot
  const runPlot = useCallback(() => {
    if (batchLoading) return;
    setAutoRunPlot(true);
  }, [batchLoading]);

  // Listens for the Enter button being pressed, if so calls runPlot (as long as
  // a textarea is not in focus)
  EnterButtonListener({
    onEnter: runPlot,
    modalOpen: chooserOpen,
    enabled: true,
  });

  const filteredRows = useMemo(() => {
    const query = filterText.trim().toLowerCase();
    if (!query) return interactiveRows;

    return interactiveRows.filter((row) => {
      const searchableValues = [
        ...visibleColumns.map((col) => row[col.key]),
        row.short_code,
      ];

      return searchableValues
        .filter((value) => value !== null && value !== undefined)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [interactiveRows, filterText, visibleColumns]);

  const resultTableColumns = useMemo(
    () => [
      ...visibleColumns.map((col) => ({
        name: col.label,
        sortable: true,
        selector: (row) => row[col.key] ?? "",
        cell: (row) => <span>{row[col.key] ?? ""}</span>,
        wrap: false,
        grow: 1,
      })),
      {
        name: "View Results",
        center: true,
        button: true,
        ignoreRowClick: true,
        allowOverflow: true,
        cell: (row) => (
          <Button href={`/short/${row.short_code}`} target="_blank" color="primary" size="sm">
            View
          </Button>
        ),
      },
    ],
    [visibleColumns]
  );

  // Use the Codon Occupancy specific short code handler
  const { userPassedShortCode, autoRunPlot, setAutoRunPlot } =
    useCodonOccupancyShortCode({
      location,
      updateMultipleSettings,
      setGroups,
    });

  // Auto-trigger analysis generation when autoRunPlot is true
  useEffect(() => {
    if (autoRunPlot) {
      if (!batchLoading && jobPairs.length > 0) {
        setAutoRunPlot(false);
        setTimeout(() => {
          handleStartJobs();
        }, 50);
      } else {
        console.log(
          "Conditions not met yet, will retry when conditions change"
        );
      }
    }
  }, [autoRunPlot, setBatchLoading, batchLoading, jobPairs.length, setAutoRunPlot]);

  // Check Connect+ status before loading the rest of the page
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const plus_status_response = await getPlusStatus(projectId);
        setPlusStatus(plus_status_response.plus_status);
      } catch (err) {
        setError("Failed to check Connect+ access");
        setPlusStatus(false);
      }
    };

    if (projectId) {
      fetchStatus();
    }
  }, [projectId]);

  // Load previously generated summary results if they're available from short code
  useEffect(() => {
    const prevSummaryResults = location.state?.shortCodePayload?.summary_results
    if (prevSummaryResults) {
      setResults(prevSummaryResults.map(formatResultRow));
      setBatchCode(location.state?.shortCodePayload?.batch_code)
    }
  }, []);

  // Scroll to loadingDiv anytime plotLoading changes
  useScrollToRef(resultsRef, batchLoading);



  // Stop global batch loading if all tasks complete
  useEffect(() => {
    if (batchLoading && activeTasks.length === 0) {
      setBatchLoading(false);
      toast.success("All batch jobs have completed.");
    }
  }, [activeTasks, batchLoading]);



  // Start Batch Jobs
  const handleStartJobs = async () => {
    if (jobPairs.length === 0) {
      toast.error("Please select at least one condition in both of the sections: 'Condition 1 selection' and 'Condition 2 selection'.");
      return;
    }

    setResults([]); 
    setFilterText("");

    var job_count_idx = 0

    const payload = {
        "project_id": projectId,
        "plot_url": '/batch_codon_occupancy',
        "batch_job": true,
        ...settings
      };

    const batch_response = await postCreateBatchCode(payload);
    setBatchCode(batch_response.batch_code);
    toast.info(`Batch job started with batch code: ${batch_response.batch_code}`);
    const newTaskIds = [];
    const selectedFiles = {}

    for (let pair of jobPairs) {
      const groups = [];
      const buildMaster = null;
      
      const payload = createPlotSpecificPayload.codonOccupancy({
        settings,
        selectedFiles,
        projectId,
        userPassedShortCode,
        analysisMode,
        groups,
        buildMaster,
      });

      payload.cond1_name = pair.cond1
      payload.cond2_name = pair.cond2

      const grp1_file_ids = filterFilesByType(apiData, existingGroups[pair.cond1], "riboseq")
      const grp2_file_ids = filterFilesByType(apiData, existingGroups[pair.cond2], "riboseq")

      payload.master_file_dict = {"group1":{"file_ids": grp1_file_ids},
                                  "group2":{"file_ids": grp2_file_ids}}
      console.log("payload", payload)
      payload.plot_type = 'comparison'
      // batch code is essentially a short code but linked to a batch of jobs rather than
      // any individual job
      payload.batch_code = batch_response.batch_code
      // job count will determine which priority the queue gets on the backend
      // lower no of jobs in a batch will lead to higher priority
      payload.job_count = jobPairs.length
      // job_count_idx is just a count applied to each job, incrementing by 1, let's the backend
      // know when the last job in the batch is being run
      payload.job_count_idx = job_count_idx
      job_count_idx += 1

      // Ensure only the last job in a batch has email notification set to true
      if (job_count_idx != jobPairs.length) {
        payload.email = false;
      }

      const response = await submitCodonOccupancyJob(payload);

      if (response.task_id) {
          newTaskIds.push(response.task_id);
          pollTaskStatus(response.task_id);
      }
    }

    setActiveTasks(newTaskIds);
    console.log("activeTasks is now", activeTasks, activeTasks.length)
    setBatchLoading(true);
  }

  // Called when batch download button is pressed
  const handleDownloadBatchResults = async () => {
    const payload = {
        "project_id": projectId,
        "plot_url": '/batch_codon_occupancy',
        "batch_code": batchCode,
      };
    const response = await postCreateBatchDownload(payload);
    if (response?.file_link) {
      const a = document.createElement('a');
      a.href = response.file_link;
      a.download = `batch_${batchCode}_results.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }


  if (plusStatus === null || (plusStatus === true && loading)) {
    return (
      <div className="page-content d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <FadeLoader color="#2F9E37" />
      </div>
    );
  }

  if (plusStatus === false) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="info" className="mb-0">
            This plot is part of Connect+ To discuss access please contact info@eirnabio.com
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb breadcrumbs={[
            { label: "Home", url: "/dashboard" },
            { label: "Select Function", url: `/projects/${projectId}/select-function` },
            { label: "Batch Codon Occupancy" },
          ]}
        />

        {/* General Options */}
        <Row className="mb-4">
          <Col lg={12}>
            {/* General Settings */}
            <GeneralSettings
              settings={settings}
              onUpdateSetting={updateSetting}
              onReset={resetSettings}
              config={codonOccupancyConfig}
              title="Codon Occupancy Settings"
              mode="batch"
            />
          </Col>
        </Row>


        {/* Conditions Selection */}
        <Row className="mb-4">
          {availableConditions.length === 0 ? (
            <Col>
              <div className="alert alert-danger" role="alert">
                No samples have been assigned to conditions yet, to fix this please visit the{" "}
                <Link to={`/projects/${projectId}/sample_groupings/`}>sample groupings page</Link>
              </div>
            </Col>
          ) : (
            <>
              <Col md={6}>
                {/* Conditions 1 table */}
                <BatchConditionSelector
                  title="Condition 1 Selection"
                  idPrefix="cond1"
                  availableConditions={availableConditions}
                  selectedConditions={selectedCond1}
                  onToggle={(cond) => handleToggle(cond, true)}
                  onSelectAll={(select) => handleSelectAll(true, select)}
                />
              </Col>
              {/* Conditions 2 table */}
              <Col md={6}>
                <BatchConditionSelector
                  title="Condition 2 Selection"
                  idPrefix="cond2"
                  availableConditions={availableConditions}
                  selectedConditions={selectedCond2}
                  onToggle={(cond) => handleToggle(cond, false)}
                  onSelectAll={(select) => handleSelectAll(false, select)}
                />
              </Col>
            </>
          )}
        </Row>

        {/* Selected Jobs Summary */}
        <Row className="mb-4">
          <Col lg={12}>
            <BatchJobPairsSummary jobPairs={jobPairs} />
          </Col>
        </Row>

        {/* Start Button */}
        <Row className="mb-4 text-center">
          <Col lg={12}>
            <ActionButton
              onClick={handleStartJobs}
              loading={batchLoading}
              customText="Start Jobs"
              loadingText="Jobs running"
              buttonType="submit"
            />
          </Col>
        </Row>


        <BatchProgressCard 
          batchLoading={batchLoading} 
          activeTasksCount={activeTasks.length} 
          batchCode={batchCode} 
        />

        {/* Results Container */}
        {(batchCode || results.length > 0) && (
          <div ref={resultsRef}>
            <Row className="mb-5">
              <Col lg={12}>
                <Card>
                  <CardHeader className="d-flex justify-content-between align-items-center">
                    {/* Left side: Title and Job ID */}
                    <div>
                      <h5 className="mb-1">
                        Result table
                      </h5>
                      <div className="text-muted d-flex align-items-center">
                        <span>Job ID: <strong>{batchCode}</strong></span>
                        <Button color="link" size="sm" className="ms-2 p-0" onClick={copyBatchURLToClipboard} title="Copy batch URL">
                          <i className="ri-clipboard-line fs-5"></i>
                        </Button>
                      </div>
                    </div>

                    {/* Right side: Download Button */}
                    {(!batchLoading) && (
                      <div>
                        <ActionButton
                          onClick={handleDownloadBatchResults}
                          loading={batchLoading}
                          customText="Download all job results"
                          loadingText="Jobs running"
                          buttonType="download"
                        />
                      </div>
                    )}
                  </CardHeader>
                  <CardBody>
                    {results.length > 0 ? (
                      <div style={{ borderRadius: "8px", overflow: "hidden" }}>
                        <DataTable
                          columns={resultTableColumns}
                          data={filteredRows}
                          customStyles={dataTableStyles}
                          sortFunction={customColumnSort}
                          responsive
                          dense
                          striped
                          highlightOnHover
                          persistTableHead
                          fixedHeader
                          fixedHeaderScrollHeight="560px"
                          pagination
                          paginationPerPage={rowsPerPage}
                          paginationRowsPerPageOptions={[10, 25, 50, 100]}
                          onChangeRowsPerPage={(newPerPage) => setRowsPerPage(newPerPage)}
                          subHeader
                          subHeaderComponent={
                            <div className="d-flex align-items-center gap-2 ms-auto">
                              {filterText && (
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={() => setFilterText("")}
                                >
                                  Clear
                                </button>
                              )}
                              <input
                                className="form-control form-control-sm"
                                placeholder="Search table..."
                                value={filterText}
                                onChange={(e) => setFilterText(e.target.value)}
                                style={{ maxWidth: 320 }}
                              />
                            </div>
                          }
                          noDataComponent={
                            <div style={{ padding: 20, color: "#6c757d", fontStyle: "italic" }}>
                              No matching results found
                            </div>
                          }
                        />
                      </div>
                    ) : (
                      <Table hover responsive className="align-middle mb-0">
                        <tbody>
                          <tr><td colSpan={visibleColumns.length + 1} className="text-center text-muted py-4">
                            Waiting for jobs to complete...
                          </td></tr>
                        </tbody>
                      </Table>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        )}

        <ToastContainer />
      </Container>
    </div>
  );
};

export default BatchCodonOccupancy;